/**
 * BOGUE 562 (Vincent 2026-07-24) — Page admin « Alertes pipeline ».
 *
 * Diagnostique et rejoue en masse les vidéos historiques dont la diarisation
 * multi-locuteurs a été écrasée à un seul « Speaker X » à cause du bogue 562
 * (regroupement des paragraphes appelé AVANT la diarisation).
 *
 * Deux modes :
 *  1) Analyser (dry_run=true) → liste les vidéos affectées sans rien changer.
 *  2) Rejouer TOUT (dry_run=false) → réenfile chaque vidéo dans la queue
 *     pipeline (reset pipeline_status → pending), le nouveau code (fix bogue
 *     562) refera la diarisation puis le regroupement dans le bon ordre.
 *
 * Route : /dashboard/admin/alerts (fondateur / admin uniquement).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import {
  Warning, SpinnerGap, ArrowsClockwise, ArrowLeft, MagnifyingGlass, PlayCircle,
} from "@phosphor-icons/react";
import { API_BASE as API } from "../lib/apiBase";

const LIMIT_DEFAULT = 200;

export default function AdminAlertsPage() {
  const { lang } = useLang();
  const [scanning, setScanning] = useState(false);
  const [replaying, setReplaying] = useState(false);
  const [report, setReport] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const runScan = useCallback(async ({ dryRun }) => {
    if (dryRun) setScanning(true); else setReplaying(true);
    try {
      const { data } = await axios.post(
        `${API}/api/videos/admin/bulk-rediarize`,
        null,
        {
          params: { dry_run: dryRun, limit: LIMIT_DEFAULT },
          withCredentials: true,
        }
      );
      setReport(data);
      if (dryRun) {
        toast.success(
          lang === "en"
            ? `${data.detected_count} affected video(s) detected.`
            : `${data.detected_count} vidéo(s) affectée(s) détectée(s).`
        );
      } else {
        toast.success(
          lang === "en"
            ? `${data.reprocessed_count} video(s) re-enqueued.`
            : `${data.reprocessed_count} vidéo(s) réenfilée(s) pour rejeu.`
        );
      }
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
        (lang === "en" ? "Scan failed" : "Analyse échouée")
      );
    } finally {
      setScanning(false);
      setReplaying(false);
      setConfirmOpen(false);
    }
  }, [lang]);

  // Analyse automatique au chargement
  useEffect(() => {
    runScan({ dryRun: true });
  }, [runScan]);

  const detected = report?.detected || [];
  const detectedCount = report?.detected_count ?? 0;
  const scope = report?.scope || "—";

  return (
    <div className="space-y-6 p-6" data-testid="admin-alerts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/dashboard/admin/diagnostics"
            className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"
            data-testid="admin-alerts-back-link"
          >
            <ArrowLeft size={16} weight="bold" />
            {lang === "en" ? "Admin Diagnostics" : "Diagnostics Admin"}
          </Link>
          <h1 className="text-2xl font-bold mt-2 flex items-center gap-2">
            <Warning size={24} weight="fill" className="text-amber-500" />
            {lang === "en" ? "Pipeline Alerts" : "Alertes pipeline"}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {lang === "en"
              ? "Detect and re-process historical videos affected by past pipeline bugs."
              : "Détecter et rejouer les vidéos historiques touchées par d'anciens bogues du pipeline."}
          </p>
        </div>
      </div>

      {/* Bug 562 card */}
      <section
        className="border border-amber-200 bg-amber-50 rounded-lg p-5"
        data-testid="bogue-562-card"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-mono bg-amber-100 text-amber-900 px-2 py-0.5 rounded">
                {lang === "en" ? "BUG 562" : "BOGUE 562"}
              </span>
              <span className="text-xs text-zinc-500">
                {lang === "en" ? "Fixed 2026-07-25 · Vincent" : "Corrigé 2026-07-25 · Vincent"}
              </span>
            </div>
            <h2 className="text-lg font-semibold text-zinc-900">
              {lang === "en"
                ? "Multi-speaker diarization overwritten to single speaker"
                : "Diarisation multi-locuteurs écrasée à un seul locuteur"}
            </h2>
            <p className="text-sm text-zinc-700 mt-1 leading-relaxed">
              {lang === "en" ? (
                <>
                  In videos processed before 2026-07-25, Whisper micro-segments were regrouped
                  into ≤ 60 s paragraphs <strong>before</strong> AssemblyAI diarization ran.
                  As a result, paragraphs mixing several speakers were later attributed to a
                  single speaker (the one with the largest temporal overlap) — minority speakers
                  vanished from the transcript. The pipeline order has been fixed. This tool
                  identifies and re-processes the historical videos affected.
                </>
              ) : (
                <>
                  Dans les vidéos traitées avant le 2026-07-25, les micro-segments Whisper
                  étaient regroupés en paragraphes ≤ 60 s <strong>avant</strong> que la diarisation
                  AssemblyAI ne s&apos;exécute. Résultat : les paragraphes couvrant plusieurs locuteurs
                  se voyaient attribuer un seul locuteur (celui au plus grand chevauchement) — les
                  locuteurs minoritaires disparaissaient. L&apos;ordre du pipeline a été corrigé. Cet
                  outil identifie et rejoue les vidéos historiques touchées.
                </>
              )}
            </p>

            <div className="flex flex-wrap gap-3 mt-4">
              <button
                type="button"
                onClick={() => runScan({ dryRun: true })}
                disabled={scanning || replaying}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white border border-zinc-300 text-sm font-medium text-zinc-800 hover:bg-zinc-50 disabled:opacity-50"
                data-testid="bogue-562-scan-btn"
              >
                {scanning ? (
                  <SpinnerGap size={16} className="animate-spin" />
                ) : (
                  <MagnifyingGlass size={16} weight="bold" />
                )}
                {lang === "en" ? "Analyze (dry run)" : "Analyser (aperçu)"}
              </button>

              <button
                type="button"
                onClick={() => setConfirmOpen(true)}
                disabled={scanning || replaying || detectedCount === 0}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="bogue-562-replay-btn"
              >
                {replaying ? (
                  <SpinnerGap size={16} className="animate-spin" />
                ) : (
                  <PlayCircle size={16} weight="fill" />
                )}
                {lang === "en"
                  ? `Re-run pipeline on ${detectedCount} video(s)`
                  : `Rejouer le pipeline sur ${detectedCount} vidéo(s)`}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Report */}
      {report && (
        <section className="bg-white border border-zinc-200 rounded-lg" data-testid="bogue-562-report">
          <header className="px-5 py-3 border-b border-zinc-200 flex items-center justify-between">
            <div className="text-sm font-medium text-zinc-800">
              {lang === "en" ? "Detected videos" : "Vidéos détectées"}{" "}
              <span className="text-zinc-500 font-normal">
                ({detectedCount}
                {report.candidates_scanned != null && (
                  <>
                    {" "}/ {report.candidates_scanned}{" "}
                    {lang === "en" ? "candidates scanned" : "candidates scannées"}
                  </>
                )}
                )
              </span>
            </div>
            <div className="text-xs text-zinc-500 font-mono" data-testid="bogue-562-scope">
              {scope}
            </div>
          </header>

          {detectedCount === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-500" data-testid="bogue-562-empty">
              {lang === "en"
                ? "No historical videos are affected by bug 562 in this scope."
                : "Aucune vidéo historique n'est affectée par le bogue 562 dans ce périmètre."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-2">
                      {lang === "en" ? "Video" : "Vidéo"}
                    </th>
                    <th className="text-left px-4 py-2">
                      {lang === "en" ? "Org" : "Org."}
                    </th>
                    <th className="text-right px-4 py-2">
                      {lang === "en" ? "AssemblyAI" : "AssemblyAI"}
                    </th>
                    <th className="text-right px-4 py-2">
                      {lang === "en" ? "In segments" : "Dans les segments"}
                    </th>
                    <th className="text-left px-4 py-2">
                      {lang === "en" ? "Status" : "Statut"}
                    </th>
                    <th className="text-left px-4 py-2">
                      {lang === "en" ? "Created" : "Créée"}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {detected.map((v) => (
                    <tr key={v.video_id} data-testid={`bogue-562-row-${v.video_id}`}>
                      <td className="px-4 py-2">
                        <Link
                          to={`/dashboard/videos/${v.video_id}`}
                          className="text-teal-700 hover:underline"
                          data-testid={`bogue-562-video-link-${v.video_id}`}
                        >
                          {v.title}
                        </Link>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-zinc-500">
                        {v.org_id}
                      </td>
                      <td className="px-4 py-2 text-right font-mono">
                        {v.speakers_detected_by_assemblyai ?? "—"}
                      </td>
                      <td className="px-4 py-2 text-right font-mono">
                        {v.distinct_speakers_in_segments === 1 ? (
                          <span className="text-red-600 font-semibold">1</span>
                        ) : (
                          v.distinct_speakers_in_segments
                        )}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-100 text-zinc-700">
                          {v.pipeline_status || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs text-zinc-500">
                        {v.created_at ? new Date(v.created_at).toLocaleDateString("fr-CA") : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Confirmation modal */}
      {confirmOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          data-testid="bogue-562-confirm-modal"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-2 mb-3">
              <ArrowsClockwise size={20} weight="bold" className="text-amber-600" />
              <h3 className="text-lg font-semibold">
                {lang === "en" ? "Confirm bulk re-run" : "Confirmer le rejeu en masse"}
              </h3>
            </div>
            <p className="text-sm text-zinc-700 leading-relaxed">
              {lang === "en" ? (
                <>
                  You are about to re-run the full pipeline on{" "}
                  <strong>{detectedCount}</strong> video(s). This will re-transcribe (Whisper),
                  re-diarize (AssemblyAI) and re-extract knowledge for each one. It may consume
                  significant BYOK credits and take several minutes per video. Continue?
                </>
              ) : (
                <>
                  Vous êtes sur le point de rejouer le pipeline complet sur{" "}
                  <strong>{detectedCount}</strong> vidéo(s). Cela relancera la transcription
                  (Whisper), la diarisation (AssemblyAI) et l&apos;extraction des connaissances pour
                  chacune. Cela peut consommer beaucoup de crédits BYOK et prendre plusieurs
                  minutes par vidéo. Continuer ?
                </>
              )}
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="px-4 py-2 text-sm rounded-md border border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                data-testid="bogue-562-confirm-cancel-btn"
              >
                {lang === "en" ? "Cancel" : "Annuler"}
              </button>
              <button
                type="button"
                onClick={() => runScan({ dryRun: false })}
                disabled={replaying}
                className="px-4 py-2 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="bogue-562-confirm-replay-btn"
              >
                {replaying && <SpinnerGap size={14} className="animate-spin" />}
                {lang === "en" ? "Yes, re-run" : "Oui, rejouer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
